import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, Collapse, Pagination, Alert } from 'react-bootstrap';
import Swal from '../helpers/swal';
import { toast } from 'react-toastify';
import campaignService from '../../services/campaign.service';
import Loading from '../common/Loading';
import {
    CampaignQueueStatusBadge,
    CampaignStatusBadge,
    getCampaignStatusKey,
} from './campaignDetailUtils';
import { useCampaign } from './useCampaign';
import { useEyeconxStore } from '../../store/eyeconxStore';
import { toProperCase } from '../process/processQueueUtils';

const STARTABLE_STATUSES = ['PENDING', 'IN_PROGRESS'];

function CampaignQueue() {
    const { campaignId } = useParams();
    const user = useEyeconxStore((state) => state.user);
    const isAdmin = user?.role === 'ADMIN';
    const { campaign, isLoading: campaignLoading, error: campaignError, refetch } = useCampaign(campaignId);

    const [itemsPerPage, setItemsPerPage] = useState(30);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [queueItems, setQueueItems] = useState([]);
    const [expandedRowId, setExpandedRowId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isStarting, setIsStarting] = useState(false);

    const isSms = campaign?.type === 'SMS';
    const queueLabel = isSms ? 'SMS Queue' : 'Email Queue';
    const receiverLabel = isSms ? 'Phone' : 'Email';
    const statusKey = getCampaignStatusKey(campaign);
    const canStartSending = STARTABLE_STATUSES.includes(statusKey);

    const fetchQueue = useCallback(() => {
        if (!campaignId || !campaign?.type || !isAdmin) return;

        setIsLoading(true);
        setError(null);

        const request = isSms
            ? campaignService.getSmsCampaignQueue(campaignId, currentPage, itemsPerPage)
            : campaignService.getEmailCampaignQueue(campaignId, currentPage, itemsPerPage);

        request
            .then((res) => {
                setQueueItems(res.data.content || []);
                setTotalPages(res.data.totalPages || 0);
                setTotalElements(res.data.totalElements || 0);
                setExpandedRowId(null);
            })
            .catch((err) => {
                setQueueItems([]);
                if (err?.response?.status === 403) {
                    setError('You do not have permission to view the campaign queue.');
                } else {
                    setError('Failed to load campaign queue. Please try again.');
                }
            })
            .finally(() => setIsLoading(false));
    }, [campaignId, campaign?.type, currentPage, itemsPerPage, isAdmin, isSms]);

    useEffect(() => {
        fetchQueue();
    }, [fetchQueue]);

    const pageStatusCounts = useMemo(() => {
        return queueItems.reduce(
            (acc, item) => {
                if (item.status === 'WAITING') acc.waiting += 1;
                else if (item.status === 'SENT') acc.sent += 1;
                else if (item.status === 'FAILED') acc.failed += 1;
                return acc;
            },
            { waiting: 0, sent: 0, failed: 0 }
        );
    }, [queueItems]);

    const handleRefresh = () => {
        refetch();
        fetchQueue();
    };

    const handleStartSending = () => {
        const channelLabel = isSms ? 'SMS' : 'email';
        const isNewsletter = String(campaign?.category || '').toUpperCase() === 'NEWSLETTER';
        const preferenceNote = isNewsletter
            ? `<p class="mb-2 text-muted small">Patients opted out of newsletters will be marked <strong>Unsubscribed</strong> and will not receive this ${channelLabel}.</p>`
            : '';
        Swal.fire({
            title: isNewsletter ? 'Start sending this newsletter?' : 'Start sending this campaign?',
            html: `
                <p class="mb-2">Waiting ${channelLabel} queue items will be queued for delivery in the background.</p>
                ${preferenceNote}
                <p class="mb-0 text-muted small">You can leave this page — EyeconX-Processor will pick them up and send them out.</p>
            `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#198754',
            confirmButtonText: 'Start Sending',
            cancelButtonText: 'Cancel',
        }).then((result) => {
            if (!result.isConfirmed) return;

            setIsStarting(true);
            campaignService
                .startSendingCampaign(campaignId)
                .then(async (res) => {
                    const queuedCount = res?.data?.queuedCount ?? 0;
                    await refetch();
                    fetchQueue();
                    toast.success(
                        `Start sending accepted for ${queuedCount} ${channelLabel} message${queuedCount === 1 ? '' : 's'}. Publishing runs in the background.`,
                        { position: toast.POSITION.BOTTOM_RIGHT }
                    );
                })
                .catch((err) => {
                    const message =
                        err?.response?.data?.errorMessage ||
                        err?.response?.data?.message ||
                        'Failed to start sending. Please try again.';
                    toast.error(message, { position: toast.POSITION.BOTTOM_RIGHT });
                })
                .finally(() => setIsStarting(false));
        });
    };

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(parseInt(event.target.value, 10));
        setCurrentPage(0);
    };

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const toggleRow = (id) => {
        setExpandedRowId((prev) => (prev === id ? null : id));
    };

    const getPaginationItems = () => {
        const items = [];
        const maxVisible = 5;

        if (totalPages < 1) return items;

        items.push(
            <Pagination.Item key={1} active={currentPage === 0} onClick={() => paginate(0)}>
                1
            </Pagination.Item>
        );

        let startPage = Math.max(2, currentPage);
        let endPage = Math.min(totalPages - 1, currentPage + 2);

        if (currentPage < 3) endPage = Math.min(totalPages - 1, maxVisible);
        if (currentPage > totalPages - 4) startPage = Math.max(2, totalPages - maxVisible + 1);

        if (startPage > 2) items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === currentPage + 1}
                    onClick={() => paginate(number - 1)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);

        if (totalPages > 1) {
            items.push(
                <Pagination.Item
                    key={totalPages}
                    active={totalPages === currentPage + 1}
                    onClick={() => paginate(totalPages - 1)}
                >
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const paginationControls =
        totalPages > 1 ? (
            <Pagination className="mb-0">
                <Pagination.First onClick={() => paginate(0)} disabled={currentPage === 0} />
                <Pagination.Prev onClick={() => paginate(currentPage - 1)} disabled={currentPage === 0} />
                {getPaginationItems()}
                <Pagination.Next
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage >= totalPages - 1}
                />
                <Pagination.Last
                    onClick={() => paginate(totalPages - 1)}
                    disabled={currentPage >= totalPages - 1}
                />
            </Pagination>
        ) : null;

    const startItem = queueItems.length > 0 ? currentPage * itemsPerPage + 1 : 0;
    const endItem = Math.min((currentPage + 1) * itemsPerPage, totalElements);

    const DetailField = ({ label, children, mono, wide }) => (
        <div className={`${wide ? 'col-12 col-lg-6' : 'col-md-6 col-lg-4'} mb-3`}>
            <div className="text-muted small mb-1">{label}</div>
            <div className={`small ${mono ? 'font-monospace text-break' : ''}`}>{children ?? '—'}</div>
        </div>
    );

    const IdCell = ({ value }) => (
        <small className="font-monospace text-break d-inline-block" style={{ maxWidth: '100%' }}>
            {value || '—'}
        </small>
    );

    const formatCount = (value) => {
        const number = Number(value);
        if (!Number.isFinite(number)) {
            return '0';
        }
        return number.toLocaleString();
    };

    const summaryCards = [
        { key: 'total', label: 'Total', value: totalElements, tone: 'total' },
        { key: 'waiting', label: 'Waiting', value: pageStatusCounts.waiting, tone: 'waiting' },
        { key: 'sent', label: 'Sent', value: pageStatusCounts.sent, tone: 'sent' },
        { key: 'failed', label: 'Failed', value: pageStatusCounts.failed, tone: 'failed' },
    ];

    if (!isAdmin) {
        return (
            <div className="container-fluid">
                <div className="pagetitle">
                    <h1>
                        <i className="bi bi-list-ol"></i> Campaign Queue
                    </h1>
                </div>
                <Alert variant="warning">
                    Campaign queue is available to admin users only.{' '}
                    <Link to="/campaign">Return to campaigns</Link>
                </Alert>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="pagetitle d-flex flex-wrap align-items-center justify-content-between gap-2">
                <h1 className="mb-0">
                    <i className={`bi ${isSms ? 'bi-chat-dots' : 'bi-envelope'} me-2`}></i>
                    {queueLabel}
                </h1>
                <div className="d-flex align-items-center gap-2">
                    <Link to="/campaign" className="campaign-queue-page__back d-inline-flex align-items-center gap-1">
                        <i className="bi bi-arrow-left"></i>
                        Back to campaigns
                    </Link>
                    {canStartSending && (
                        <Button
                            variant="success"
                            size="sm"
                            type="button"
                            onClick={handleStartSending}
                            disabled={isStarting || campaignLoading || isLoading}
                        >
                            <i className="bi bi-send-fill me-1"></i>
                            {isStarting ? 'Starting…' : 'Start Sending'}
                        </Button>
                    )}
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleRefresh}
                        disabled={campaignLoading || isLoading}
                    >
                        <i className="bi bi-arrow-clockwise me-1"></i>
                        Refresh
                    </Button>
                </div>
            </div>

            {campaignLoading ? (
                <Loading />
            ) : campaignError ? (
                <Alert variant="warning">{campaignError}</Alert>
            ) : (
                campaign && (
                    <>
                        <div className="campaign-stats-header mb-3">
                            <div className="campaign-stats-header__identity">
                                <div className="campaign-stats-header__title-row">
                                    <span
                                        className="campaign-stats-header__name"
                                        title={campaign.name || 'Untitled campaign'}
                                    >
                                        {campaign.name || 'Untitled campaign'}
                                    </span>
                                    <span
                                        className={`badge campaign-queue-type-badge ${
                                            isSms
                                                ? 'bg-success bg-opacity-10 text-success'
                                                : 'bg-primary bg-opacity-10 text-primary'
                                        }`}
                                    >
                                        <i className={`bi ${isSms ? 'bi-chat-dots' : 'bi-envelope'} me-1`}></i>
                                        {campaign.type}
                                    </span>
                                    <span className="badge bg-light text-dark border campaign-queue-type-badge">
                                        {toProperCase(campaign.category)}
                                    </span>
                                    <CampaignStatusBadge
                                        status={campaign.status}
                                        statusCode={campaign.statusCode}
                                    />
                                </div>
                                <div className="campaign-stats-header__meta text-muted">
                                    <span>{campaign.clinicName || '—'}</span>
                                    {campaign.title ? (
                                        <>
                                            <span className="campaign-stats-header__dot" aria-hidden="true">
                                                ·
                                            </span>
                                            <span title={campaign.title}>{campaign.title}</span>
                                        </>
                                    ) : null}
                                    <span className="campaign-stats-header__dot" aria-hidden="true">
                                        ·
                                    </span>
                                    <span className="font-monospace" title={campaign.id}>
                                        {campaign.id || '—'}
                                    </span>
                                </div>
                            </div>

                            <div className="campaign-stats-header__metrics" aria-label="Campaign queue summary">
                                {summaryCards.map((card) => (
                                    <div
                                        key={card.key}
                                        className={`campaign-stats-metric campaign-stats-metric--${card.tone}`}
                                        title={
                                            card.key === 'total'
                                                ? 'Total items in queue'
                                                : `${card.label} on this page`
                                        }
                                    >
                                        <span className="campaign-stats-metric__value">
                                            {formatCount(card.value)}
                                        </span>
                                        <span className="campaign-stats-metric__label">{card.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="card shadow-sm border-0 campaign-queue-table-card">
                            <div className="card-body p-2 p-md-3">
                                <div className="campaign-stats-results-toolbar mb-2">
                                    <div className="campaign-stats-results-toolbar__title">
                                        <i className="bi bi-table text-primary"></i>
                                        <h5 className="card-title mb-0">
                                            Queue Items
                                            <span className="text-muted fw-normal">
                                                ({formatCount(totalElements)} total)
                                            </span>
                                        </h5>
                                    </div>
                                </div>

                                {isLoading ? (
                                    <Loading />
                                ) : error && queueItems.length === 0 ? (
                                    <Alert variant="warning">{error}</Alert>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-bordered table-hover align-middle mb-0 process-queue-table">
                                            <thead className="table-light">
                                                <tr>
                                                    <th style={{ width: '120px' }}>Status</th>
                                                    <th>Patient</th>
                                                    <th>{receiverLabel}</th>
                                                    <th>Queue ID</th>
                                                    <th>Patient ID</th>
                                                    <th style={{ width: '220px' }}>Created</th>
                                                    <th style={{ width: '56px' }}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {queueItems.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={7} className="text-center text-muted py-5">
                                                            <i className="bi bi-inbox d-block fs-3 mb-2"></i>
                                                            No queue items found.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    queueItems.map((item) => (
                                                        <React.Fragment key={item.id}>
                                                            <tr
                                                                className={
                                                                    expandedRowId === item.id ? 'table-active' : ''
                                                                }
                                                            >
                                                                <td>
                                                                    <CampaignQueueStatusBadge status={item.status} />
                                                                </td>
                                                                <td>
                                                                    <div className="fw-medium small">
                                                                        {item.patientName || '—'}
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <small className="text-break campaign-queue-receiver">
                                                                        {item.receiver || '—'}
                                                                    </small>
                                                                </td>
                                                                <td>
                                                                    <IdCell value={item.id} />
                                                                </td>
                                                                <td>
                                                                    <IdCell value={item.patientId} />
                                                                </td>
                                                                <td>
                                                                    <small>{item.createdAt || '—'}</small>
                                                                </td>
                                                                <td className="text-center">
                                                                    <Button
                                                                        variant={
                                                                            expandedRowId === item.id
                                                                                ? 'outline-secondary'
                                                                                : 'outline-primary'
                                                                        }
                                                                        size="sm"
                                                                        onClick={() => toggleRow(item.id)}
                                                                        title={
                                                                            expandedRowId === item.id
                                                                                ? 'Collapse'
                                                                                : 'Expand'
                                                                        }
                                                                        aria-expanded={expandedRowId === item.id}
                                                                    >
                                                                        <i
                                                                            className={`bi bi-chevron-${
                                                                                expandedRowId === item.id
                                                                                    ? 'up'
                                                                                    : 'down'
                                                                            }`}
                                                                        ></i>
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                            <tr className="p-0 border-top-0">
                                                                <td colSpan={7} className="p-0 border-top-0">
                                                                    <Collapse in={expandedRowId === item.id}>
                                                                        <div className="p-3 bg-light border-top">
                                                                            <div className="row">
                                                                                <DetailField label="Patient" wide>
                                                                                    {item.patientName || '—'}
                                                                                </DetailField>
                                                                                <DetailField label={receiverLabel} wide>
                                                                                    {item.receiver || '—'}
                                                                                </DetailField>
                                                                                <DetailField label="Sender" wide>
                                                                                    {item.sender || '—'}
                                                                                </DetailField>
                                                                                <DetailField label="Queue ID" mono wide>
                                                                                    {item.id}
                                                                                </DetailField>
                                                                                <DetailField label="Patient ID" mono wide>
                                                                                    {item.patientId}
                                                                                </DetailField>
                                                                                <DetailField label="Campaign ID" mono wide>
                                                                                    {item.campaignId}
                                                                                </DetailField>
                                                                                <DetailField label="Clinic ID" mono wide>
                                                                                    {item.clinicId}
                                                                                </DetailField>
                                                                                <DetailField label="Status">
                                                                                    <CampaignQueueStatusBadge
                                                                                        status={item.status}
                                                                                    />
                                                                                </DetailField>
                                                                                <DetailField label="Status Code">
                                                                                    {item.statusCode}
                                                                                </DetailField>
                                                                                <DetailField label="Created">
                                                                                    {item.createdAt}
                                                                                </DetailField>
                                                                                <DetailField label="Modified">
                                                                                    {item.modifiedAt}
                                                                                </DetailField>
                                                                            </div>
                                                                        </div>
                                                                    </Collapse>
                                                                </td>
                                                            </tr>
                                                        </React.Fragment>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                                    <div className="d-flex flex-wrap align-items-center gap-3">
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="small text-muted">Show</span>
                                            <select
                                                className="form-select form-select-sm"
                                                style={{ width: '80px' }}
                                                value={itemsPerPage}
                                                onChange={handleItemsPerPageChange}
                                            >
                                                {[10, 30, 50, 100].map((n) => (
                                                    <option key={n} value={n}>
                                                        {n}
                                                    </option>
                                                ))}
                                            </select>
                                            <span className="small text-muted">entries</span>
                                        </div>
                                        <span className="text-muted small">
                                            Showing {formatCount(startItem)}–{formatCount(endItem)} of{' '}
                                            {formatCount(totalElements)}
                                        </span>
                                    </div>
                                    {paginationControls}
                                </div>
                            </div>
                        </div>
                    </>
                )
            )}
        </div>
    );
}

export default CampaignQueue;
